import LrDialog, { LrDialogBaseProps } from 'components/LrDialog';
import * as React from 'react';
import { Claim } from 'types/losses.type';
import { Form, Formik, Field } from 'formik';
import Grid from '@mui/material/Unstable_Grid2';
import { Select, TextInput } from 'components/Input';
import { currencyMask, dateMask } from 'helpers/masks';
import { Spacing } from 'components/Layout';
import LrButton from 'components/LrButton/components/Button';
import { claimDataSchema } from './ValidationSchema';
import { Accordion, AccordionDetails, AccordionSummary, IconButton, MenuItem, Typography } from '@mui/material';
import { currencyToNumber, formatDollar } from 'helpers/formatters';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LrFileUpload, LrFileUploadAPI } from '../../../../LrFileItems';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDocumentTypesByAction, getPerilTypesByCode, getPerilTypesList } from 'application/redux/selectors';
import { ACTION_TYPES } from '../../../../../constants';
import { SubmissionDetails } from 'types/submission.type';
import { updateClaim } from 'services/claims';
import EditIcon from '@mui/icons-material/Edit';
import { LossFormData } from '../../LossForm';
import { PerilType } from '../../../../../types/types.type';

// lossId: number;

interface Props extends LrDialogBaseProps {
    claimData?: Claim;
    submission?: SubmissionDetails;
    onSubmit?: (submittedClaim: Claim) => void;
    productLineCode: string;
    canEdit?: boolean;
}

const defaultProps: Partial<Props> = {
    title: 'Claim Data',
};

export interface ClaimFormData {
    vendorId: string;
    claimNumber: string;
    claimStatus: string;
    dateOpened: string;
    dateClosed: string;
    dateReopened: string;
    cause: string;
    perilType: string;
    paidIndemnity: string;
    paidExpense: string;
    incurredIndemnity: string;
    incurredExpense: string;
    recoveredIndemnity: string;
    recoveredExpense: string;
    totalIncurred: string;
    totalPaid: string;
    dateClaimReported: string;
    catCode: string;
    adjusterName: string;
    adjusterEmail: string;
    descriptionOfEvent: string;
    id: string;
    lumenCause: string;
    dateOfLoss: string;
}

const claimToFormState = (claim: Claim) => {
    const state = {} as ClaimFormData;
    state.vendorId = claim.vendorId;
    state.claimNumber = claim.claimNumber;
    state.claimStatus = claim.claimStatus;
    state.dateOpened = claim.dateOpened;
    state.dateClosed = claim.dateClosed;
    state.dateReopened = claim.dateReopened;
    state.cause = claim.cause;
    state.perilType = claim.perilType;
    state.paidIndemnity = formatDollar(claim.paidIndemnity);
    state.paidExpense = formatDollar(claim.paidExpense);
    state.incurredIndemnity = formatDollar(claim.incurredIndemnity);
    state.incurredExpense = formatDollar(claim.incurredExpense);
    state.recoveredIndemnity = formatDollar(claim.recoveredIndemnity);
    state.recoveredExpense = formatDollar(claim.recoveredExpense);
    state.totalIncurred = formatDollar(claim.totalIncurred);
    state.totalPaid = formatDollar(claim.totalPaid);
    state.dateClaimReported = claim.dateClaimReported;
    state.catCode = claim.catCode;
    state.adjusterName = claim.adjusterName;
    state.adjusterEmail = claim.adjusterEmail;
    state.descriptionOfEvent = claim.descriptionOfEvent;
    state.id = claim.id;
    state.lumenCause = claim.lumenCause;
    state.dateOfLoss = claim.dateOfLoss;
    return state;
};

const ClaimDataDialog: React.FunctionComponent<Props> = (props) => {
    const { claimData, submission, caption, isOpen, canEdit, title, productLineCode, onCancel, onSubmit } = props;

    const [editing, setEditing] = useState<boolean>(false);
    const currentActionType = ACTION_TYPES.SUBMISSION_LOSSES;
    const fileTypeOptions = useSelector(getDocumentTypesByAction(currentActionType));
    const perilTypes = useSelector(getPerilTypesByCode) as PerilType[];

    const fileUploadAPI = useRef<LrFileUploadAPI>(null);
    const readonly = !editing || !canEdit;

    const onDialogSubmit = async (data: ClaimFormData) => {
        if (submission && editing) {
            try {
                const docs = await fileUploadAPI?.current?.doSubmit();
                const {
                    paidIndemnity,
                    paidExpense,
                    incurredIndemnity,
                    incurredExpense,
                    recoveredIndemnity,
                    recoveredExpense,
                    totalIncurred,
                    totalPaid,
                    ...restOfProps
                } = data;
                const payload = {
                    ...restOfProps,
                    paidIndemnity: Number(currencyToNumber(paidIndemnity)),
                    paidExpense: Number(currencyToNumber(paidExpense)),
                    incurredIndemnity: Number(currencyToNumber(incurredIndemnity)),
                    incurredExpense: Number(currencyToNumber(incurredExpense)),
                    recoveredIndemnity: Number(currencyToNumber(recoveredIndemnity)),
                    recoveredExpense: Number(currencyToNumber(recoveredExpense)),
                    totalIncurred: Number(currencyToNumber(totalIncurred)),
                    totalPaid: Number(currencyToNumber(totalPaid)),
                } as Claim;
                await updateClaim(data.id, {
                    claim: payload,
                    documents: docs || [],
                });
                fileUploadAPI?.current?.clearFiles();
                onSubmit && onSubmit(payload);
            } catch (e) {
                // TODO what todo
            }
        } else {
            onDialogCancel();
        }
    };

    const onDialogCancel = () => {
        setEditing(false);
        onCancel && onCancel();
    };

    return (
        <LrDialog isOpen={isOpen} caption={caption} onCancel={onDialogCancel} closeButton title={title} width="800px">
            <Formik
                initialValues={claimToFormState(claimData || ({} as Claim))}
                isInitialValid={true}
                onSubmit={onDialogSubmit}
                validationSchema={readonly ? undefined : claimDataSchema}
                enableReinitialize
            >
                {({ errors, isValid, isSubmitting, touched, values }) => (
                    <Form>
                        <Grid container justifyContent={'space-between'}>
                            <Grid>
                                <Typography variant={'h2'}>
                                    <b>
                                        {`Total Incurred: ${formatDollar(
                                            Number(currencyToNumber(values.incurredIndemnity)) +
                                                Number(currencyToNumber(values.incurredExpense)),
                                            0
                                        )}`}
                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                        {`Total Paid: ${formatDollar(
                                            Number(currencyToNumber(values.paidIndemnity)) +
                                                Number(currencyToNumber(values.paidExpense)),
                                            0
                                        )}`}
                                    </b>
                                </Typography>
                            </Grid>
                            <Grid>
                                <IconButton size={'small'} onClick={() => setEditing(!editing)}>
                                    <EditIcon style={{ fontSize: 15 }} />
                                </IconButton>
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid xs={4}>
                                <Field
                                    as={TextInput}
                                    name="claimNumber"
                                    title="Claim Number"
                                    readOnly={readonly}
                                    errorMessage={touched.claimNumber && errors.claimNumber}
                                />
                            </Grid>
                            <Grid xs={2}>
                                <Field
                                    as={TextInput}
                                    name="claimStatus"
                                    title="Status"
                                    readOnly={readonly}
                                    errorMessage={touched.claimStatus && errors.claimStatus}
                                />
                            </Grid>
                            <Grid xs={6}>
                                <Field
                                    as={TextInput}
                                    name="cause"
                                    title="Cause"
                                    readOnly={readonly}
                                    errorMessage={touched.cause && errors.cause}
                                />
                            </Grid>
                            <Grid xs={6}>
                                {readonly && (
                                    <TextInput
                                        value={
                                            values.perilType in perilTypes
                                                ? perilTypes[values.perilType].displayValue
                                                : ''
                                        }
                                        title={productLineCode === 'GENERAL_LIABILITY' ? 'Incident Type' : 'Peril Type'}
                                        readOnly={readonly}
                                        errorMessage={touched.cause && errors.cause}
                                    />
                                )}
                                {!readonly && (
                                    <Field
                                        as={Select}
                                        name="perilType"
                                        title={productLineCode === 'GENERAL_LIABILITY' ? 'Incident Type' : 'Peril Type'}
                                        disabled={readonly}
                                        errorMessage={touched.cause && errors.cause}
                                    >
                                        <MenuItem disabled value="">
                                            Select Type
                                        </MenuItem>
                                        {Object.values(perilTypes)
                                            .filter((type) => type.productLineCode === productLineCode)
                                            .map((type) => (
                                                <MenuItem key={type.id} value={type.code.toString()}>
                                                    {type.displayValue}
                                                </MenuItem>
                                            ))}
                                    </Field>
                                )}
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="catCode"
                                    title="CAT#"
                                    readOnly={readonly}
                                    errorMessage={touched.catCode && errors.catCode}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="adjusterName"
                                    title="Adjuster Name"
                                    readOnly={readonly}
                                    errorMessage={touched.adjusterName && errors.adjusterName}
                                />
                            </Grid>
                            <Grid xs={6}>
                                <Field
                                    as={TextInput}
                                    name="adjusterEmail"
                                    title="Adjuster Email"
                                    readOnly={readonly}
                                    errorMessage={touched.adjusterEmail && errors.adjusterEmail}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="dateClaimReported"
                                    title="Date Reported"
                                    placeholder="MM/DD/YYYY"
                                    readOnly={readonly}
                                    errorMessage={touched.dateClaimReported && errors.dateClaimReported}
                                    mask={dateMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="dateOpened"
                                    title="Date Opened"
                                    placeholder="MM/DD/YYYY"
                                    readOnly={readonly}
                                    errorMessage={touched.dateOpened && errors.dateOpened}
                                    mask={dateMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="dateClosed"
                                    title="Date Closed"
                                    placeholder="MM/DD/YYYY"
                                    readOnly={readonly}
                                    errorMessage={touched.dateClosed && errors.dateClosed}
                                    mask={dateMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="dateReopened"
                                    title="Date Re-Opened"
                                    placeholder="MM/DD/YYYY"
                                    readOnly={readonly}
                                    errorMessage={touched.dateReopened && errors.dateReopened}
                                    mask={dateMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="incurredIndemnity"
                                    title="Incurred Indemnity"
                                    readOnly={readonly}
                                    errorMessage={touched.incurredIndemnity && errors.incurredIndemnity}
                                    mask={currencyMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="incurredExpense"
                                    title="IncurredExpense"
                                    readOnly={readonly}
                                    errorMessage={touched.incurredExpense && errors.incurredExpense}
                                    mask={currencyMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="paidIndemnity"
                                    title="Paid Indemnity"
                                    readOnly={readonly}
                                    errorMessage={touched.paidIndemnity && errors.paidIndemnity}
                                    mask={currencyMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="paidExpense"
                                    title="Paid Expense"
                                    readOnly={readonly}
                                    errorMessage={touched.paidExpense && errors.paidExpense}
                                    mask={currencyMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    value={
                                        Number(currencyToNumber(values.incurredIndemnity)) -
                                        Number(currencyToNumber(values.paidIndemnity))
                                    }
                                    title="Reserved Indemnity"
                                    readOnly
                                    mask={currencyMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    value={
                                        Number(currencyToNumber(values.incurredExpense)) -
                                        Number(currencyToNumber(values.paidExpense))
                                    }
                                    title="Reserved Expense"
                                    readOnly
                                    mask={currencyMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="recoveredIndemnity"
                                    title="Recovered Indemnity"
                                    readOnly={readonly}
                                    errorMessage={touched.recoveredIndemnity && errors.recoveredIndemnity}
                                    mask={currencyMask}
                                />
                            </Grid>
                            <Grid xs={3}>
                                <Field
                                    as={TextInput}
                                    name="recoveredExpense"
                                    title="Recovered Expense"
                                    readOnly={readonly}
                                    errorMessage={touched.recoveredExpense && errors.recoveredExpense}
                                    mask={currencyMask}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <Field
                                    as={TextInput}
                                    name="descriptionOfEvent"
                                    multiline
                                    rows={5}
                                    title="Description"
                                    readOnly={readonly}
                                    placeholder="Add a message"
                                    errorMessage={touched.descriptionOfEvent && errors.descriptionOfEvent}
                                />
                            </Grid>
                        </Grid>
                        {editing && (
                            <>
                                <Spacing height="16px" />
                                <Grid xs={12}>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} id={'addDocuments header'}>
                                            <Typography>Add Documents</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <LrFileUpload
                                                fileTypeOptions={fileTypeOptions}
                                                fileTypeNeedsYear={{}}
                                                ref={fileUploadAPI}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            </>
                        )}
                        <Spacing height="32px" />
                        <Grid container spacing={0} alignItems="center">
                            <LrButton
                                lrType="primary"
                                type="submit"
                                disabled={!editing && !isValid}
                                isLoading={isSubmitting}
                            >
                                {!editing ? 'OK' : 'Submit'}
                            </LrButton>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </LrDialog>
    );
};

ClaimDataDialog.defaultProps = defaultProps;
export default ClaimDataDialog;
